import React, { FC, useRef } from 'react'
import { gsap } from 'gsap/all'
import { IGridCardItemProps } from './types'

export const GridCardItem: FC<IGridCardItemProps> = ({
  item,
  hideMarker = true,
  textSize = 'text-lg sm:text-xl md:text-2xl',
  centerText = true,
  disableHoverOnMobile = true,
  disableHover = false,
}) => {
  const cardRef = useRef<HTMLLIElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const radiusRef = useRef({ r: 0 })
  const posRef = useRef({ x: 0, y: 0 })

  const isMobile = () => {
    if (typeof window === 'undefined') return false
    return window.innerWidth < 768 || !window.matchMedia('(hover: hover)').matches
  }

  const updateClipPath = () => {
    if (overlayRef.current) {
      const clip = `circle(${radiusRef.current.r}px at ${posRef.current.x}px ${posRef.current.y}px)`
      overlayRef.current.style.clipPath = clip
      overlayRef.current.style.setProperty('-webkit-clip-path', clip)
    }
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLLIElement>) => {
    if (disableHover || (disableHoverOnMobile && isMobile())) return
    if (!cardRef.current) return
    const rect = cardRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    posRef.current = { x, y }

    gsap.to(radiusRef.current, {
      r: 160,
      duration: 0.3,
      ease: 'power2.out',
      onUpdate: updateClipPath,
      overwrite: 'auto',
    })
  }

  const handleMouseEnter = (e: React.MouseEvent<HTMLLIElement>) => {
    if (disableHover || (disableHoverOnMobile && isMobile())) return
    if (!cardRef.current) return
    const rect = cardRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    posRef.current = { x, y }

    gsap.to(radiusRef.current, {
      r: 160,
      duration: 0.4,
      ease: 'power2.out',
      onUpdate: updateClipPath,
    })
  }

  const handleMouseLeave = () => {
    if (disableHover || (disableHoverOnMobile && isMobile())) return
    gsap.to(radiusRef.current, {
      r: 0,
      duration: 0.35,
      ease: 'power2.in',
      onUpdate: updateClipPath,
    })
  }

  const hoverBorderClass = disableHover
    ? ''
    : disableHoverOnMobile
      ? 'md:hover:border-primary'
      : 'hover:border-primary'

  return (
    <li
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`group relative overflow-hidden w-full flex items-center justify-center min-h-[120px] sm:min-h-[150px] md:min-h-[180px] p-6 sm:p-8 md:p-10 rounded-container-base md:rounded-container-large border-2 border-outline-low ${hoverBorderClass} transition-colors duration-300 ${
        disableHover ? '' : 'cursor-default'
      } bg-surface/50 dark:bg-layer-lowest/50`}
    >
      {/* BASE LAYER */}
      <div
        className={`w-full flex items-center ${
          centerText ? 'justify-center text-center' : ''
        } gap-4 sm:gap-5 ${textSize} font-bold leading-tight text-on-surface`}
      >
        {!hideMarker && <span className="w-3 h-3 bg-primary rounded-[3px] shrink-0" />}
        <span className={centerText ? 'text-center' : 'flex-1'}>{item}</span>
      </div>

      {/* OVERLAY LAYER */}
      {!disableHover && (
        <div
          ref={overlayRef}
          className={`pointer-events-none absolute inset-0 bg-primary dark:bg-on-surface p-6 sm:p-8 md:p-10 flex items-center ${
            centerText ? 'justify-center text-center' : ''
          } gap-4 sm:gap-5 ${textSize} font-bold leading-tight text-on-primary-unstable dark:text-surface z-10 select-none`}
          style={{
            clipPath: 'circle(0px at 0px 0px)',
            WebkitClipPath: 'circle(0px at 0px 0px)',
          }}
        >
          {!hideMarker && (
            <span className="w-3 h-3 bg-on-primary-unstable dark:bg-primary rounded-[3px] shrink-0" />
          )}
          <span className={centerText ? 'text-center' : 'flex-1'}>{item}</span>
        </div>
      )}
    </li>
  )
}

export default GridCardItem
